import React, { useState } from 'react';
import { Breadcrumb, Col, Row, Skeleton, Tooltip, Dropdown, Menu, Input, Modal, Popconfirm } from 'antd';
import { Header } from 'antd/es/layout/layout';
import { FaArrowRight, FaExclamation } from 'react-icons/fa';
import { FiMoreVertical, FiX } from 'react-icons/fi';
import useAdBlockStore from 'store/AdBlockStore';
import Button from './Button';
import { Link, useParams } from '@reach/router';
import SelectDropdown from './SelectDropdown';
import { navigate } from '@reach/router';
import useKeypress from "react-use-keypress";
import { toast, ToastContainer } from 'react-toastify';
import { resetAdblock, markAdblockToReview, getSimilarAdBlockProducts } from 'api/adblock';
import { getCurrentUserAPI } from 'api';
import { useMainContext } from "contexts"

const TOAST_CONFIG = {
    position: "top-right",
    autoClose: 3000,
    closeOnClick: true,
    pauseOnHover: true,
    theme: "dark"
};

const PageActions = () => {
    const {
        dneBlock,
        rejectBlock,
        navigateNextUnapproved,
        activeBlockData,
        circularName,
        pageList,
        adblockList,
        currentPageID,
        currentAdBlockID,
        activePageData,
        isLoading,
        isApproving,
        isRejecting,
        isDNELoading
    } = useAdBlockStore();

    const {
          liveCounterData,
          setLiveCounterData
        } = useMainContext();
    
    const [reviewNote, setReviewNote] = useState('');
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleResetAdblock = async () => {
        try {
            const result = await resetAdblock(currentAdBlockID);
            if (result.success) {
                toast.success('Block has been reset', TOAST_CONFIG);
                window.location.reload();
            } else {
                toast.error('Failed to reset block', TOAST_CONFIG);
            }
        } catch (error) {
            toast.error('Failed to reset block', TOAST_CONFIG);
        }
    };

    const handleFindImageMatch = async () => {
        try {
            setIsSubmitting(true);
            const result = await getSimilarAdBlockProducts(currentAdBlockID);
            if (result.success) {
                toast.success('Image match found', TOAST_CONFIG);
                window.location.reload();
            } else {
                toast.info('No similar image found', TOAST_CONFIG);
            }
        } catch (error) {
            toast.error('Failed to find image match', TOAST_CONFIG);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDNE = async () => {
        const is_success = await dneBlock();
        if (is_success) {
            const result = await getCurrentUserAPI();
            if (result.success) {
                setLiveCounterData(result.results);
            }
        }
    }

    const handleReviewSubmit = async () => {
        if (!reviewNote.trim()) {
            toast.warning('Please add review notes', TOAST_CONFIG);
            return;
        }
        
        setIsSubmitting(true);
        try {
            const result = await markAdblockToReview(currentAdBlockID, reviewNote);
            if (result.success) {
                toast.info('Block is marked for review', { ...TOAST_CONFIG, autoClose: 5000 });
                setReviewNote("");
                setIsReviewModalOpen(false);
            } else {
                toast.error('Failed to mark for review', TOAST_CONFIG);
            }
        } catch (error) {
            toast.error('Failed to mark for review', TOAST_CONFIG);
        } finally {
            setIsSubmitting(false);
        }
    };

    const moreOptionsMenuItems = [
        {
            key: 'image-match',
            label: 'CHECK IM',
            onClick: handleFindImageMatch,
            disabled: activeBlockData?.is_dupe || activeBlockData?.products_approved,
        },
        {
            key: 'review',
            label: 'REVIEW',
            onClick: () => setIsReviewModalOpen(true),
        },
        {
            key: 'reset',
            label: <span className='menu-reset-item'>RESET</span>,
            onClick: handleResetAdblock,
            danger: true,
        },
    ];

    const moreOptionsMenu = (
        <>
            <Menu style={{ backgroundColor: '#1f1f1f', border: '1px solid #333' }} items={moreOptionsMenuItems} />
        </>
    );

    const { circular, page, adblock } = useParams();

    const actionButtons = [
        {
            id: 'dne',
            type: activeBlockData?.do_not_enter_f ? 'danger' : 'default',
            icon: <FaExclamation />,
            label: 'DNE',
            tooltip: 'Mark as Do Not Enter (Alt+X)',
            onClick: handleDNE,
            confirmTitle: 'Confirm DNE',
            confirmDescription: 'Are you sure you want to mark this block as Do Not Enter?'
        },
        {
            id: 'reject',
            type: activeBlockData?.rejected ? 'danger' : 'default',
            icon: <FiX size={20} />,
            label: activeBlockData?.rejected ? 'REJECTED' : 'REJECT',
            tooltip: 'Reject this block (Alt+R)',
            onClick: rejectBlock
        },
        {
            id: 'next',
            type: 'default',
            icon: <FaArrowRight />,
            label: '',
            tooltip: 'Go to next unapproved block',
            onClick: navigateNextUnapproved
        },
    ];

    const buttonStates = {
        dne: isDNELoading,
        reject: isRejecting,
        next: isLoading,
    };

    const isAnyLoading = isLoading || isApproving || isRejecting || isDNELoading;

    useKeypress(["x", "X"], (event) => event.altKey && dneBlock());
    useKeypress(["r", "R"], (event) => event.altKey && rejectBlock());

    return (
        <Header style={{
            background: '#131313',
            color: 'white',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '0 24px',
            height: 64,
            display: 'flex',
            alignItems: 'center',
        }}>
            <Row align="middle" style={{ height: '100%', width: '100%' }}>
                <Col
                    span={8}
                    style={{
                        paddingRight: 16,
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                    }}
                >
                    <Breadcrumb
                        items={[{
                            title: circularName
                                ? <Link to={`/circular/${circular}/${page}/${adblock}`}>{circularName}</Link>
                                : <Skeleton.Input title active style={{ display: 'inline-block', width: 120 }} />,
                        },
                        {
                            title: activePageData?.pageType || (
                                <Skeleton.Input title active style={{ display: 'inline-block', width: 120 }} />
                            ),
                        }
                        ]}
                        style={{ color: '#909090', fontSize: '14px' }}
                    />
                </Col>
                <Col span={6} style={{ display: 'flex', alignItems: 'center', height: '100%', gap: 8 }}>
                    <SelectDropdown
                        items={pageList?.map(page => ({
                            index: page.pageNum,
                            id: page.id,
                            label: `${page.id}`,
                            approved: page.crop_approved,
                            variant: page.is_dupe
                        })) || []}
                        currentID={currentPageID}
                        label="Select Page"
                        suffix={pageList?.length ? `${pageList.length} Pages` : ''}
                        disabled={isLoading}
                        onSelect={id => {
                            useAdBlockStore.getState().setCurrentPageID(id);
                            navigate(`/edit/${circular}/${id}/0`);
                        }}
                    />
                    <SelectDropdown
                        items={adblockList?.map((adblock, index) => ({
                            index: index + 1,
                            id: adblock.id,
                            approved: adblock.products_approved && adblock.offers_approved,
                            variant: adblock.dupe_of,
                            dne: adblock.do_not_enter_f,
                            rejected: adblock.rejected,
                            is_dupe: adblock.is_dupe
                        })) || []}
                        currentID={currentAdBlockID}
                        label="Select Block"
                        suffix={adblockList?.length ? `${adblockList.length} Ad Blocks` : ''}
                        disabled={isLoading}
                        onSelect={id => {
                            useAdBlockStore.getState().setCurrentAdBlockID(id);
                            navigate(`/edit/${circular}/${currentPageID}/${id}`);
                        }}
                    />
                </Col>
                <Col span={10} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%', gap: 16 }}>
                    <span style={{ color: '#909090', fontSize: 13 }}>
                        Page: {pageList?.indexOf(pageList.find(p => p.id === currentPageID)) + 1 || 0} / {pageList?.length || 0}
                    </span>
                    <span style={{ color: '#909090', fontSize: 13 }}>
                        Completed: {adblockList?.filter(block => block.products_approved && block.offers_approved).length || 0} / {adblockList?.length || 0}
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Dropdown overlay={moreOptionsMenu} trigger={['click']} placement="bottomRight">
                            <Tooltip title="More options">
                                <Button
                                    type="default"
                                    icon={<FiMoreVertical size={15} />}
                                    disabled={isAnyLoading}
                                />
                            </Tooltip>
                        </Dropdown>
                        {actionButtons.map((button) => (
                            button.confirmTitle ? (
                                <Popconfirm
                                    key={button.id}
                                    title={button.confirmTitle}
                                    description={button.confirmDescription}
                                    onConfirm={button.onClick}
                                    okText="Confirm"
                                    cancelText="Cancel"
                                >
                                    <Tooltip title={button.tooltip}>
                                        <Button
                                            type={button.type}
                                            icon={button.icon}
                                            disabled={buttonStates[button.id] || isAnyLoading}
                                        >
                                            {button.label}
                                        </Button>
                                    </Tooltip>
                                </Popconfirm>
                            ) : (
                                <Tooltip key={button.id} title={button.tooltip}>
                                    <Button
                                        type={button.type}
                                        icon={button.icon}
                                        onClick={button.onClick}
                                        disabled={buttonStates[button.id] || isAnyLoading}
                                    >
                                        {button.label}
                                    </Button>
                                </Tooltip>
                            )
                        ))}
                    </div>
                </Col>
            </Row>

            <Modal
                title="Add Review Notes"
                open={isReviewModalOpen}
                onOk={handleReviewSubmit}
                onCancel={() => {
                    setIsReviewModalOpen(false);
                    setReviewNote('');
                }}
                okText="Submit"
                cancelText="Cancel"
                confirmLoading={isSubmitting}
                okButtonProps={{ disabled: !reviewNote.trim() }}
                centered
                width={480}
                styles={{
                    body: { backgroundColor: '#1f1f1f', color: 'white', padding: '24px' },
                    mask: { backgroundColor: 'rgba(0, 0, 0, 0.75)' }
                }}
            >
                <Input.TextArea
                    rows={5}
                    placeholder="Describe the issue or provide review notes..."
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    autoFocus
                    style={{
                        backgroundColor: '#2a2a2a',
                        color: 'white',
                        border: '1px solid #444',
                        borderRadius: 4,
                        fontSize: 14
                    }}
                />
            </Modal>

            <ToastContainer />
        </Header>
    )
}

export default PageActions;